/**
 * What a licence IS, in this product.
 *
 * A licence is ONE APPLIANCE: a hardware address, a term, a set of the 76
 * features the XOP appliance can license, and six port counters. It is a real
 * row in crm-api (`licences`), and the artefact it produces is the ZIP of
 * `license.yml` + `license.yml.signed` that `Feature.update_license` accepts.
 *
 * REPLACED the 2026-08-30 model, which projected a licence out of the
 * customer's agreement and shipped a signed JSON envelope. The format was never
 * unsettled — `10/web/lib/feature.rb` has pinned it for years — and no appliance
 * in the field could read what that design produced. See
 * `xop-edge-staff/docs/superpowers/specs/2026-09-10-licensing-design.md`.
 *
 * THIS PORTAL SIGNS NOTHING. Key material lives only in crm-api: both portals
 * serve browsers, and a copy in each would put the secret that mints licences
 * for the whole fleet on two internet-facing boxes.
 *
 * The pure parts live here because the BFF, the licences page, the customer
 * card and the overview all read them, and a status four surfaces derive four
 * ways is four chances to disagree.
 */

import {
  LICENCE_SECTION_LABELS,
  LICENCE_SECTIONS,
  licenceSectionOf,
  type LicenceSection,
  type PriceListCategory,
  type PriceListPricingMode,
  type PriceListUnit,
} from "./priceList.js";

/* ── The appliance's vocabulary ───────────────────────────────────────── */

export const LICENCE_PORT_KEYS = [
  "total",
  "tdm",
  "audioConferencing",
  "webConferencing",
  "networkSwitches",
  "audioRecording",
] as const;

export type LicencePortKey = (typeof LICENCE_PORT_KEYS)[number];
export type LicencePorts = Record<LicencePortKey, number>;

/**
 * One row of the licence catalogue — a PRICING FEATURE, not an appliance key
 * (2026-09-15, later). `GET /api/v1/licences/catalog` serves the command
 * center's Pricing rows in the two licence categories (Value Added Services,
 * Platform Extensions); each one names the appliance key it switches on in
 * `licenceKey`, or `null` when XOP has not linked it yet. The appliance's own
 * vocabulary — all 76 keys — rides beside the rows as `applianceKeys`, so a
 * key already on a licence that no row maps can still be recognised.
 *
 * `code` is the 4-digit internal identity and `partNumber` the printed one;
 * NEITHER is a thing a tile prints — the reader sees `name`. `category` and
 * `section` are crm-api's, relayed and never invented by the BFF: a client
 * groups on `section` and derives it from `category` only for a row that
 * arrived without one — see `licenceCatalogSections`.
 */
export interface LicenceCatalogFeature {
  id: number;
  code: string;
  name: string;
  section?: LicenceSection;
  /** The appliance key this feature licenses (`conferencing`), or null while
   * XOP has not linked the price-list row to one — such a row is shown but
   * cannot be selected. */
  licenceKey: string | null;
  category: PriceListCategory | null;
  partNumber: string;
  unit: PriceListUnit;
  pricingMode: PriceListPricingMode;
  /** Minor units (cents). Relayed for completeness; a reseller's picker
   * prints no money. */
  priceCents: number;
  percentRate: number | null;
  currency: string;
  active: boolean;
}

/** `GET /api/crm/licences/catalog`. */
export interface LicenceCatalog {
  features: LicenceCatalogFeature[];
  /** Every key the appliance can license, linked or not. */
  applianceKeys: string[];
}

/** One of the two sections the builder shows, with the features in it. */
export interface LicenceCatalogSection {
  section: LicenceSection;
  label: string;
  features: LicenceCatalogFeature[];
}

/** Which section a catalogue row belongs in: what crm-api said, or — for a
 * row that reached us without a `section` — what its category implies. */
export function licenceFeatureSection(
  feature: Pick<LicenceCatalogFeature, "section" | "category">,
): LicenceSection {
  return feature.section ?? licenceSectionOf(feature.category);
}

/**
 * The catalogue as the builder shows it: the two sections in the appliance's
 * fixed order — Value Added Services, then Platform Extensions — each with its
 * features in the order crm-api sent them. A section with nothing in it is
 * omitted rather than printed as a heading over nothing. Every feature lands
 * in exactly one section, so nothing the appliance can license is lost by the
 * grouping.
 */
export function licenceCatalogSections(
  features: readonly LicenceCatalogFeature[],
): LicenceCatalogSection[] {
  return LICENCE_SECTIONS.map((section) => ({
    section,
    label: LICENCE_SECTION_LABELS[section],
    features: features.filter((feature) => licenceFeatureSection(feature) === section),
  })).filter((group) => group.features.length > 0);
}

/**
 * The keys on a licence that NO catalogue row links to — a "legacy" key: it
 * was set before the price list learned it, or the row that named it was
 * unlinked since. The picker shows each as a greyed, selected tile that can
 * only be switched OFF; nothing offers to switch one on.
 */
export function legacyLicenceKeys(
  selectedKeys: readonly string[],
  features: readonly Pick<LicenceCatalogFeature, "licenceKey">[],
): string[] {
  const linked = new Set(features.map((feature) => feature.licenceKey).filter(Boolean));
  return selectedKeys.filter((key) => !linked.has(key));
}

/** `mass_notification` → "Mass notification": what a legacy key reads as when
 * nothing else names it. Never a code, never the raw key. */
export function humaniseLicenceKey(key: string): string {
  const words = key.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** One feature a licence grants, placed for display. */
export interface LicenceFeatureGroup {
  section: LicenceSection;
  label: string;
  features: Array<{ key: string; name: string; legacy: boolean }>;
}

/**
 * A LICENCE's features in the two sections — for every surface that lists
 * what an appliance may run (the customer page's card, the list) rather than
 * picks it. Each key is placed by the catalogue row that links to it; a key no
 * row links is a legacy key under Platform Extensions, named by the licence's
 * own `features` row or, failing that, humanised. Empty sections are omitted.
 */
export function licenceFeatureGroups(
  licence: Pick<ResellerLicence, "featureKeys" | "features">,
  catalog: readonly LicenceCatalogFeature[],
): LicenceFeatureGroup[] {
  const byKey = new Map<string, LicenceCatalogFeature>();
  for (const feature of catalog) {
    if (feature.licenceKey && !byKey.has(feature.licenceKey)) byKey.set(feature.licenceKey, feature);
  }
  const named = new Map(licence.features.map((feature) => [feature.key, feature.name]));
  const placed = licence.featureKeys.map((key) => {
    const row = byKey.get(key);
    if (row) return { section: licenceFeatureSection(row), key, name: row.name, legacy: false };
    return {
      section: "platform_extensions" as const,
      key,
      name: named.get(key) || humaniseLicenceKey(key),
      legacy: true,
    };
  });
  return LICENCE_SECTIONS.map((section) => ({
    section,
    label: LICENCE_SECTION_LABELS[section],
    features: placed
      .filter((feature) => feature.section === section)
      .map(({ key, name, legacy }) => ({ key, name, legacy })),
  })).filter((group) => group.features.length > 0);
}

/** A feature this licence actually grants. */
export interface LicenceFeature {
  key: string;
  name: string;
}

export interface LicenceIssueSummary {
  serial: string;
  issuedAt: string;
  issuedByLabel: string;
  sha256: string;
  downloadCount: number;
  /** NULL = no cap (the default since 2026-09-11); every pull is journaled. */
  downloadLimit: number | null;
  downloadsRemaining: number | null;
}

export type LicenceRecordStatus = "draft" | "issued" | "revoked";

/** The approval workflow. Since 2026-09-15 a TRIAL licence needs nobody and a
 * FULL licence always waits for XOP's executives (every command-center super
 * admin is emailed). This portal can REQUEST, never decide.
 *  not_required — a trial, or never needed approval
 *  pending      — sent to XOP, nothing signed
 *  approved     — approved for exactly the current terms
 *  rejected     — declined; `note` says why */
export type LicenceApprovalStatus = "not_required" | "pending" | "approved" | "rejected";

export interface LicenceApproval {
  status: LicenceApprovalStatus;
  requestedAt: string | null;
  requestedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  note: string | null;
}

/** The licence TYPE (CEO, 2026-09-15), chosen on the builder and stored by
 * crm-api — never inferred from the end date any more.
 *  trial — 30 days, signs at once; only for a customer holding no other licence
 *  full  — any term or perpetual; ALWAYS waits for approval by XOP's executives */
export type LicenceKind = "trial" | "full";

/** The longest label crm-api stores, and the cap the BFF's zod schema applies. */
export const LICENCE_LABEL_MAX = 120;

/**
 * What a NEW licence is called before anybody types.
 *
 * The label came back on 2026-09-16, a day after it was taken away: a row in
 * the licences list, a card waiting for XOP's executives and the mail that
 * reaches them all have to say WHICH licence they are about, and "2 licences
 * awaiting approval" does not. It is a HUMAN NAME and nothing derives meaning
 * from it. It is not part of `signedTerms` either, so editing it never voids
 * an approval — a reseller may rename a licence at any time.
 *
 * The suggestion is deliberately SHORT, and made only of what the builder
 * already knows: the customer the appliance belongs to, plus the one word that
 * tells two of that customer's licences apart. Here the customer is the
 * RESELLER'S OWN end customer, which is exactly what makes the same rule read
 * correctly in the command center too.
 */
export function suggestLicenceLabel(input: {
  customerName: string;
  kind: LicenceKind;
  /** A renewal replaces an expiring licence on the same appliance. */
  renewal?: boolean;
}): string {
  const name = input.customerName.trim().replace(/\s+/g, " ");
  if (!name) return "";
  const suffix = input.renewal ? " · renewal" : input.kind === "trial" ? " · trial" : "";
  return `${name}${suffix}`.slice(0, LICENCE_LABEL_MAX);
}

/** What pressing Issue WOULD do with the current terms, so the button can say
 * "Generate trial licence" or "Send for approval" before anything is sent. */
export interface LicenceTrial {
  eligible: boolean;
  /** May THIS customer still get a trial? True only while the customer holds
   * no other licence. */
  available: boolean;
  /** The end of this appliance's one 30-day window. */
  windowEndsOn: string;
  startedOn: string | null;
}

/** "issued" means a file exists. "approval_requested" means NOTHING was signed
 * and XOP's executives have been emailed. Read it — never assume Issue produced a file. */
export type LicenceIssueOutcome = "issued" | "approval_requested" | "already_pending";

/** One pull of a licence file, refusals included. */
export interface LicenceDownloadRow {
  id: number;
  licenceId: number;
  serial: string;
  actorType: string;
  actorLabel: string;
  ip: string | null;
  refused: boolean;
  downloadedAt: string;
}

/**
 * RENEWAL (2026-09-15). A licence is renewed by drafting its SUCCESSOR — a
 * new full licence for the same appliance, same features, same ports — from
 * 30 days before it expires. crm-api decides all of it: whether this licence
 * may be renewed now (`renewable`), when the window opens (`renewableFrom`),
 * whether a successor already exists and how far it has got, and — on the
 * successor itself — which licence it renews. This portal only reads.
 */
export interface LicenceRenewalSuccessor {
  id: number;
  /** The successor's file serial once generated; null while it is a draft
   * or waiting for XOP's executives. */
  serial: string | null;
  status: LicenceRecordStatus;
  approvalStatus: LicenceApprovalStatus;
  expiresOn: string | null;
  kind: LicenceKind;
}

export interface LicenceRenewal {
  /** May "Renew licence" be pressed right now? */
  renewable: boolean;
  /** The first day the window is open, or null when it never will be (a
   * perpetual or unissued licence). */
  renewableFrom: string | null;
  /** How many days before expiry the window opens — 30. */
  windowDays: number;
  /** The renewal drafted from this licence, if one exists. */
  successor: LicenceRenewalSuccessor | null;
  /** On a successor: the licence it renews. */
  renews: { id: number; serial: string | null; expiresOn: string | null } | null;
}

/** What an older crm-api that knows nothing of renewal reads as. */
export const NO_RENEWAL: LicenceRenewal = {
  renewable: false,
  renewableFrom: null,
  windowDays: 30,
  successor: null,
  renews: null,
};

/** One appliance's licence, as every surface in the portal reads it. */
export interface ResellerLicence {
  id: number;
  customerId: number;
  customerName: string;
  /** How a reseller tells one of a customer's boxes from another. */
  label: string;
  hardwareAddress: string;
  /** ISO date, or null for a PERPETUAL licence — the file then carries no
   * `expires:` key at all. */
  expiresOn: string | null;
  perpetual: boolean;
  status: LicenceRecordStatus;
  kind: LicenceKind;
  featureKeys: string[];
  features: LicenceFeature[];
  ports: LicencePorts;
  currentIssue: LicenceIssueSummary | null;
  approval: LicenceApproval;
  trial: LicenceTrial;
  renewal: LicenceRenewal;
  notes: string | null;
}

/** What the builder saves. Every field optional so the form can send just what
 * changed. Snake_case because it goes straight to crm-api. */
export interface LicenceInput {
  kind?: LicenceKind;
  label?: string;
  hardware_address?: string;
  expires_on?: string | null;
  notes?: string | null;
  feature_keys?: string[];
  ports_total?: number;
  ports_tdm?: number;
  ports_audio_conferencing?: number;
  ports_web_conferencing?: number;
  ports_network_switches?: number;
  ports_audio_recording?: number;
}

/* ── Dates ────────────────────────────────────────────────────────────── */

/**
 * Normalise a date crm-api sent into `YYYY-MM-DD`, or null if it is not one.
 *
 * TWO FORMATS ARRIVE, which is the whole reason this exists.
 * `CustomerSerializer` emits deal dates as `MM/DD/YYYY` (the Agreement modal
 * carries its own `toDateInput` for exactly this), while the raw columns and
 * every write go out as ISO. A term that was wrong because one caller happened
 * to receive the American form would be wrong in the artefact a customer's
 * software enforces, so both are understood in one place.
 */
export function normalizeLicenceDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();

  const mdy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  const iso = mdy
    ? `${mdy[3]!}-${mdy[1]!}-${mdy[2]!}`
    : /^(\d{4}-\d{2}-\d{2})/.exec(trimmed)?.[1];
  if (!iso) return null;

  // `2026-02-31` parses in JS (it rolls into March) but is not a date anyone
  // typed on purpose. Round-tripping catches it.
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10) === iso ? iso : null;
}

/** Whole days from `today` until the end of `endsOn`. Null when there is no
 * usable end date. Negative once the date has passed. */
export function daysUntilExpiry(
  endsOn: string | null | undefined,
  today = new Date(),
): number | null {
  const iso = normalizeLicenceDate(endsOn);
  if (!iso) return null;
  const end = new Date(`${iso}T00:00:00Z`).getTime();
  const start = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.floor((end - start) / 86_400_000);
}

/**
 * "Expiring" is 60 days out — long enough that a reseller can still act on it,
 * which is the only reason to show the state at all.
 */
export const EXPIRING_WINDOW_DAYS = 60;

/* ── Two statuses, deliberately ───────────────────────────────────────── */

/**
 * An AGREEMENT's state, derived from its end date.
 *
 * `unknown` is right here: a customer agreement with no end date recorded is a
 * gap in the data, not a promise of perpetuity. Read by the customers list
 * badge and the customer detail page's agreement rows.
 *
 * Do NOT use this for an appliance licence — see {@link applianceLicenceStatus}.
 */
export type LicenceStatus = "active" | "expiring" | "expired" | "unknown";

export function licenceStatus(
  endsOn: string | null | undefined,
  today = new Date(),
): LicenceStatus {
  const days = daysUntilExpiry(endsOn, today);
  if (days === null) return "unknown";
  if (days < 0) return "expired";
  if (days <= EXPIRING_WINDOW_DAYS) return "expiring";
  return "active";
}

/**
 * An APPLIANCE LICENCE's state.
 *
 * `perpetual`, not `unknown`: on a licence, a missing end date is a DECISION
 * with a precise meaning — the file omits `expires:` entirely and the box never
 * lapses. Encoding it as "unknown" would report a deliberate term as a data
 * gap, and encoding it as a far-future date would make the appliance lie about
 * it in that year.
 *
 * That is the whole reason this is a second function rather than a reuse of
 * {@link licenceStatus}: the same absent date means different things on the two
 * records, and one function returning one word for both would have to be wrong
 * about one of them.
 */
export type ApplianceLicenceStatus = "active" | "expiring" | "expired" | "perpetual";

export function applianceLicenceStatus(
  licence: Pick<ResellerLicence, "expiresOn">,
  today = new Date(),
): ApplianceLicenceStatus {
  const days = daysUntilExpiry(licence.expiresOn, today);
  if (days === null) return "perpetual";
  if (days < 0) return "expired";
  if (days <= EXPIRING_WINDOW_DAYS) return "expiring";
  return "active";
}

/** "Oct 1, 2026" from a date-only ISO value, read as a LOCAL date so it never
 * slips a day west of UTC. Anything else is returned as it came. */
export function formatLicenceDate(iso: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!match) return iso ?? "";
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Today as a date-only ISO value, in LOCAL time — what `renewableFrom`
 * compares against. */
function todayIso(today: Date): string {
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

/**
 * What comes NEXT for this licence, as one fact every surface prints the same
 * way — the customer card's line, the list's chip, the builder's caption.
 *
 * The successor wins over the window: once a renewal exists, "renew now" is
 * the wrong thing to say however open the window is. `serial` falls back to
 * `#id` only for a successor that has no file yet.
 */
export type LicenceRenewalNext =
  | { kind: "generated"; successorId: number; serial: string; validUntil: string | null }
  | { kind: "pending"; successorId: number; serial: string }
  | { kind: "rejected"; successorId: number; serial: string }
  | { kind: "drafted"; successorId: number; serial: string }
  | { kind: "renewable" }
  | { kind: "opens"; on: string }
  | null;

export function licenceRenewalNext(
  licence: Pick<ResellerLicence, "renewal">,
  today = new Date(),
): LicenceRenewalNext {
  const { renewal } = licence;
  const successor = renewal.successor;
  if (successor) {
    const serial = successor.serial ?? `#${successor.id}`;
    if (successor.status === "issued") {
      return { kind: "generated", successorId: successor.id, serial, validUntil: successor.expiresOn };
    }
    if (successor.approvalStatus === "pending") return { kind: "pending", successorId: successor.id, serial };
    if (successor.approvalStatus === "rejected") return { kind: "rejected", successorId: successor.id, serial };
    return { kind: "drafted", successorId: successor.id, serial };
  }
  if (renewal.renewable) return { kind: "renewable" };
  const from = normalizeLicenceDate(renewal.renewableFrom);
  if (from && from > todayIso(today)) return { kind: "opens", on: from };
  return null;
}

/**
 * Sort comparator: soonest expiry first, perpetual last, then by customer.
 *
 * A perpetual licence sorts LAST rather than first. Both the licences page and
 * the overview's "expires next" exist to answer "what needs attention", and a
 * licence that never expires never does — putting it at the top because its
 * date is missing would bury the one that lapses on Friday.
 */
export function byExpiry(a: ResellerLicence, b: ResellerLicence): number {
  const left = normalizeLicenceDate(a.expiresOn);
  const right = normalizeLicenceDate(b.expiresOn);
  if (left && right && left !== right) return left < right ? -1 : 1;
  if (left && !right) return -1;
  if (!left && right) return 1;
  return a.customerName.localeCompare(b.customerName);
}

/* ── What the appliance will silently ignore ──────────────────────────── */

/**
 * `Feature.read_license` force-disables these when their DERIVED port count is
 * zero (`feature.rb:492-495`, then `:521-541`) and reports it NOWHERE — the
 * licence installs cleanly and the feature is simply off.
 *
 * `webcast` is in that loop upstream but is not a licensable key, so its branch
 * is dead. `web_conferencing` is not gated at all: the flag survives with no
 * capacity, which is a warning rather than a refusal.
 */
export const PORT_GATED_FEATURES: ReadonlyArray<{ feature: string; port: LicencePortKey }> = [
  { feature: "conferencing", port: "audioConferencing" },
  { feature: "mass_notification", port: "audioConferencing" },
];

export function portGateErrors(
  licence: Pick<ResellerLicence, "featureKeys" | "ports">,
): string[] {
  const selected = new Set(licence.featureKeys);

  return PORT_GATED_FEATURES.filter(
    ({ feature, port }) => selected.has(feature) && licence.ports[port] <= 0,
  ).map(
    ({ feature, port }) =>
      `${feature} is licensed but ${port} is 0 — the appliance will switch it off without saying so.`,
  );
}

export function portGateWarnings(
  licence: Pick<ResellerLicence, "featureKeys" | "ports">,
): string[] {
  if (!licence.featureKeys.includes("web_conferencing")) return [];
  if (licence.ports.webConferencing > 0) return [];

  return [
    "web_conferencing is licensed with 0 web ports — the feature stays on but has no capacity.",
  ];
}

/**
 * THE HARDWARE ADDRESS — accept what people type, store what the box reports.
 *
 * `Feature.get_system_id` returns MD5 over the box's network config as
 * hex8-hex8-hex8-hex8 (lowercase) — unless libvalfilecheck.so is present, in
 * which case it returns an uppercase colon MAC. The appliance compares whichever
 * it reports as an EXACT STRING, and it does so BEFORE checking the signature,
 * so a licence carrying `aa-bb-cc-dd-ee-ff` fails at the customer's console even
 * though it names the right card.
 *
 * So the rule ACCEPTS the standard notations (IEEE 802 EUI-48 in colon, hyphen,
 * Cisco dotted or bare form — exactly one notation, never mixed separators; the
 * system id in either case) and NORMALISES to the canonical form. What is stored
 * is never looser than what the appliance compares against.
 *
 * The same rule lives in crm-api and the command center (2026-09-15); change
 * all three together.
 */
const ADDRESS_SYSTEM_ID = /^[0-9a-f]{8}-[0-9a-f]{8}-[0-9a-f]{8}-[0-9a-f]{8}$/i;
const MAC_NOTATIONS: readonly RegExp[] = [
  /^[0-9a-f]{2}(?::[0-9a-f]{2}){5}$/i, // AA:BB:CC:DD:EE:FF
  /^[0-9a-f]{2}(?:-[0-9a-f]{2}){5}$/i, // AA-BB-CC-DD-EE-FF
  /^[0-9a-f]{4}(?:\.[0-9a-f]{4}){2}$/i, // AABB.CCDD.EEFF (Cisco)
  /^[0-9a-f]{12}$/i, // AABBCCDDEEFF
];

export const HARDWARE_ADDRESS_INVALID_MESSAGE =
  "Not a hardware address the appliance reports. Use its system id (xxxxxxxx-xxxxxxxx-xxxxxxxx-xxxxxxxx) or its MAC address (e.g. AA:BB:CC:DD:EE:FF).";

type ParsedAddress = { canonical: string; problem: null } | { canonical: null; problem: string | null };

function parseHardwareAddress(input: string): ParsedAddress {
  const compact = input.replace(/\s+/g, "");
  // Nothing typed is not an error the field should shout about.
  if (compact === "") return { canonical: null, problem: null };

  if (ADDRESS_SYSTEM_ID.test(compact)) return { canonical: compact.toLowerCase(), problem: null };

  if (!MAC_NOTATIONS.some((notation) => notation.test(compact))) {
    return { canonical: null, problem: HARDWARE_ADDRESS_INVALID_MESSAGE };
  }

  const hex = compact.replace(/[:.-]/g, "").toUpperCase();
  if (/^0{12}$/.test(hex)) {
    return { canonical: null, problem: "00:00:00:00:00:00 is not an address any network card reports." };
  }
  if (/^F{12}$/.test(hex)) {
    return { canonical: null, problem: "That is the broadcast address, not a network card's own address." };
  }
  // The I/G bit: least-significant bit of the first octet set = a group address.
  if ((parseInt(hex.slice(0, 2), 16) & 1) === 1) {
    return { canonical: null, problem: "That is a multicast address, not a network card's own address." };
  }
  return { canonical: hex.match(/../g)!.join(":"), problem: null };
}

/** The canonical form the appliance reports — lowercase system id or uppercase
 * colon MAC — or null when the input is empty or not an acceptable address. */
export function normalizeHardwareAddress(input: string): string | null {
  return parseHardwareAddress(input).canonical;
}

/** Why this input cannot be used, in a sentence; null when it is fine. Empty
 * input is null too, so an untouched field is not red — callers that REQUIRE
 * an address check for emptiness themselves. */
export function hardwareAddressProblem(input: string): string | null {
  return parseHardwareAddress(input).problem;
}

/** True when the input normalises to an address the appliance reports. */
export function isValidHardwareAddress(address: string): boolean {
  return normalizeHardwareAddress(address) !== null;
}

/** The name the downloaded ZIP lands under. crm-api sends its own
 * `content-disposition`; this is the fallback when a caller builds one. */
export function licenceFileName(
  licence: Pick<ResellerLicence, "customerName"> & { serial?: string | null },
): string {
  const slug =
    licence.customerName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "licence";
  return `${slug}-${licence.serial || "licence"}.zip`;
}

/**
 * Would these terms issue as a TRIAL? Mirrors crm-api's `Licence#trial?`: an
 * end date inside the appliance's one 30-day window (`trial.windowEndsOn`).
 * Perpetual never is. ISO dates compare lexicographically — no Date parsing.
 */
export function isTrialTerm(expiresOn: string | null | undefined, windowEndsOn: string): boolean {
  return Boolean(expiresOn) && (expiresOn as string) <= windowEndsOn;
}
